import { useEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const STATIC_HEADLINE_TOP = "Your brand isn't";
const STATIC_HEADLINE_BOTTOM = "like the rest.";

const PHASE_1_BODY_LINES = [
  { text: "You built something different.", start: 0.05, end: 0.14, tone: "body setup" },
  { text: "You spent real money making sure people see it.", start: 0.11, end: 0.2, tone: "body setup" },
  { text: "But did the campaign travel as far as it could?", start: 0.18, end: 0.28, tone: "body question" },
  { text: "And are you already thinking about the next one?", start: 0.26, end: 0.34, tone: "body question" },
];

const ROTATING_SUFFIXES = [
  "chances to be seen.",
  "ways to resonate.",
  "cuts to test against each other.",
];

const lerpOpacity = (base, progress) => base + progress * (1 - base);

function getWordProgress(progress, start, end, index, count) {
  const duration = Math.max(end - start, 0.001);
  const local = gsap.utils.clamp(0, 1, (progress - start) / duration);
  const step = 1 / Math.max(count, 1);
  const wordStart = index * step;
  return gsap.utils.clamp(0, 1, (local - wordStart) / Math.max(step, 0.001));
}

export default function Manifesto() {
  const rootRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const phase1WordRefs = useRef([]);
  const stopIntroWordRefs = useRef([]);
  const line3WordRefs = useRef([]);
  const rotatingPrefixRef = useRef(null);
  const rotatingSuffixRefs = useRef([]);

  const phase1Words = useMemo(() => PHASE_1_BODY_LINES.map((line) => line.text.split(" ")), []);
  const stopIntroWords = useMemo(() => "That campaign still has twenty stories in it.".split(" "), []);
  const line3Words = useMemo(() => "No new shoot. No new budget. All from the same footage.".split(" "), []);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setReducedMotion(media.matches);
    onChange();
    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }
    if (typeof media.addListener === "function") {
      media.addListener(onChange);
      return () => media.removeListener(onChange);
    }
  }, []);


  useEffect(() => {
    if (reducedMotion || !rootRef.current) return;

    const ctx = gsap.context(() => {
      const stopNode = rootRef.current.querySelector("[data-stop]");
      const introNode = rootRef.current.querySelector("[data-phase2-intro]");
      const phase2Body = rootRef.current.querySelector("[data-phase2-body]");

      const trigger = ScrollTrigger.create({
        trigger: rootRef.current,
        start: "top top",
        end: "+=360%",
        pin: true,
        scrub: 0.8,
        onEnter: () => {
          document.body.classList.remove("hero-nav-dark");
          document.body.classList.add("manifesto-nav-dark");
        },
        onEnterBack: () => {
          document.body.classList.remove("hero-nav-dark");
          document.body.classList.add("manifesto-nav-dark");
        },
        onLeave: () => document.body.classList.remove("manifesto-nav-dark"),
        onLeaveBack: () => document.body.classList.remove("manifesto-nav-dark"),
        onUpdate: ({ progress }) => {
          const mappedProgress = progress <= 0.86
            ? progress
            : gsap.utils.clamp(0, 1, 0.86 + ((progress - 0.86) / 0.14) * 1.35);
          const darkPhaseEnd = 0.37;
          const dark = mappedProgress < darkPhaseEnd - 0.005;
          rootRef.current.classList.toggle("manifesto-light", mappedProgress >= darkPhaseEnd);
          document.body.classList.toggle("manifesto-nav-dark", dark);
          if (!dark) document.body.classList.remove("manifesto-nav-dark");

          const mutedPast = 0.25;
          const mutedNext = 0.18;
          const leadIn = 0.025;

          PHASE_1_BODY_LINES.forEach((line, lineIdx) => {
            const words = phase1WordRefs.current[lineIdx] || [];
            if (mappedProgress < line.start - leadIn) return words.forEach((n) => n && (n.style.opacity = "0"));
            if (mappedProgress < line.start) return words.forEach((n) => n && (n.style.opacity = `${mutedNext}`));
            if (mappedProgress <= line.end) {
              words.forEach((node, wordIdx) => {
                if (!node) return;
                const p = getWordProgress(mappedProgress, line.start, line.end, wordIdx, words.length);
                node.style.opacity = `${lerpOpacity(mutedNext, p)}`;
              });
              return;
            }
            words.forEach((n) => n && (n.style.opacity = `${mutedPast}`));
          });

          rootRef.current.style.setProperty("--manifesto-phase1-opacity", mappedProgress >= darkPhaseEnd ? "0" : "1");

          if (stopNode) stopNode.style.opacity = mappedProgress < darkPhaseEnd ? "0" : mappedProgress < 0.49 ? "1" : `${gsap.utils.clamp(0, 1, 1 - (mappedProgress - 0.49) / 0.025)}`;
          if (introNode) introNode.style.opacity = mappedProgress < darkPhaseEnd ? "0" : mappedProgress < 0.49 ? "1" : `${gsap.utils.clamp(0, 1, 1 - (mappedProgress - 0.49) / 0.025)}`;

          stopIntroWordRefs.current.forEach((node, idx) => {
            if (!node) return;
            if (mappedProgress < 0.37 - leadIn) node.style.opacity = "0";
            else if (mappedProgress < 0.37) node.style.opacity = "0.2";
            else if (mappedProgress <= 0.46) node.style.opacity = `${lerpOpacity(0.2, getWordProgress(mappedProgress, 0.37, 0.46, idx, stopIntroWordRefs.current.length))}`;
            else node.style.opacity = "1";
          });

          if (phase2Body) phase2Body.style.opacity = mappedProgress >= 0.52 ? "1" : "0";

          // line 1
          const line1Words = rootRef.current.querySelectorAll("[data-line1] .manifesto-word");
          line1Words.forEach((node, idx) => {
            if (mappedProgress < 0.52 - leadIn) node.style.opacity = "0";
            else if (mappedProgress < 0.52) node.style.opacity = "0.18";
            else if (mappedProgress <= 0.57) node.style.opacity = `${lerpOpacity(0.18, getWordProgress(mappedProgress, 0.52, 0.57, idx, line1Words.length))}`;
            else node.style.opacity = "1";
          });

          if (rotatingPrefixRef.current) {
            rotatingPrefixRef.current.style.opacity = mappedProgress < 0.57 ? "0" : mappedProgress <= 0.61 ? `${gsap.utils.clamp(0, 1, (mappedProgress - 0.57) / 0.04)}` : "1";
          }

          const phraseOpacity = (p, fadeInStart, fadeInEnd, holdEnd, fadeOutEnd) => {
            if (p < fadeInStart) return 0;
            if (p <= fadeInEnd) return gsap.utils.clamp(0, 1, (p - fadeInStart) / Math.max(fadeInEnd - fadeInStart, 0.001));
            if (p <= holdEnd) return 1;
            if (p <= fadeOutEnd) return gsap.utils.clamp(0, 0.35, 1 - (p - holdEnd) / Math.max(fadeOutEnd - holdEnd, 0.001));
            return 0;
          };

          if (rotatingSuffixRefs.current[0]) rotatingSuffixRefs.current[0].style.opacity = `${phraseOpacity(mappedProgress, 0.61, 0.625, 0.65, 0.665)}`;
          if (rotatingSuffixRefs.current[1]) rotatingSuffixRefs.current[1].style.opacity = `${phraseOpacity(mappedProgress, 0.665, 0.68, 0.705, 0.72)}`;
          if (rotatingSuffixRefs.current[2]) rotatingSuffixRefs.current[2].style.opacity = `${mappedProgress < 0.70 ? 0 : mappedProgress <= 0.72 ? gsap.utils.clamp(0, 1, (mappedProgress - 0.70) / 0.02) : 1}`;

          line3WordRefs.current.forEach((node, idx) => {
            if (!node) return;
            if (mappedProgress < 0.76 - leadIn) node.style.opacity = "0";
            else if (mappedProgress < 0.76) node.style.opacity = "0.18";
            else if (mappedProgress <= 0.86) node.style.opacity = `${lerpOpacity(0.18, getWordProgress(mappedProgress, 0.76, 0.86, idx, line3WordRefs.current.length))}`;
            else node.style.opacity = "1";
          });
        },
      });

      return () => trigger.kill();
    }, rootRef);

    return () => {
      document.body.classList.remove("manifesto-nav-dark");
      ctx.revert();
      ScrollTrigger.getAll().forEach((t) => t.trigger === rootRef.current && t.kill());
    };
  }, [reducedMotion]);


  return (
    <section ref={rootRef} className="manifesto-section" aria-label="Manifesto section">
      <div className="manifesto-inner">
        <div className="manifesto-phase manifesto-phase-dark">
          <h2 className="manifesto-static-headline">{STATIC_HEADLINE_TOP}<br />{STATIC_HEADLINE_BOTTOM}</h2>
          {PHASE_1_BODY_LINES.map((line, lineIdx) => (
            <p className={`manifesto-line manifesto-tone-body ${line.tone.includes("question") ? "manifesto-line-question" : "manifesto-line-setup"}`} key={line.text}>
              {phase1Words[lineIdx].map((word, wordIdx) => (
                <span className="manifesto-word" key={`${word}-${wordIdx}`} ref={(el) => ((phase1WordRefs.current[lineIdx] ??= [])[wordIdx] = el)}>
                  {word}{" "}
                </span>
              ))}
            </p>
          ))}
        </div>

        <div className="manifesto-phase manifesto-phase-light">
          <div className="manifesto-stop-center">
            <h2 data-stop className="manifesto-stop">Stop.</h2>
            <p data-phase2-intro className="manifesto-intro">
              {stopIntroWords.map((word, idx) => (
                <span className="manifesto-word" key={`${word}-${idx}`} ref={(el) => (stopIntroWordRefs.current[idx] = el)}>{word}{" "}</span>
              ))}
            </p>
          </div>

          <div data-phase2-body className="manifesto-phase2-body">
            <p data-line1 className="manifesto-line manifesto-tone-reframe-headline">
              {["We're here to help", "you find them."].map((line, lineIdx) => (
                <span key={lineIdx} className="manifesto-line-chunk">
                  {line.split(" ").map((word, idx, arr) => (
                    <span className="manifesto-word" key={`${lineIdx}-${word}-${idx}`}>{word}{idx < arr.length - 1 ? " " : ""}</span>
                  ))}
                  {lineIdx === 0 ? <br /> : null}
                </span>
              ))}
            </p>

            <p className="manifesto-line manifesto-rotating-line">
              <span className="manifesto-rotating-prefix" ref={rotatingPrefixRef}>Twenty more&nbsp;</span>
              <span className="manifesto-rotating-suffix-wrap">
                {ROTATING_SUFFIXES.map((line, idx) => (
                  <span key={line} className="manifesto-rotating-suffix" ref={(el) => (rotatingSuffixRefs.current[idx] = el)}>{line}</span>
                ))}
              </span>
            </p>

            <p className="manifesto-line manifesto-tone-body manifesto-tone-body-lg">
              {line3Words.map((word, wordIdx) => (
                <span className="manifesto-word" key={`${word}-${wordIdx}`} ref={(el) => (line3WordRefs.current[wordIdx] = el)}>{word}{" "}</span>
              ))}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
